import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Grid,
  Tooltip,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Pagination,
  TextField,
  InputAdornment,
} from '@mui/material';
import { useMain } from '../../hooks/useMain';
import ShowLoader from 'src/components/CustomComponents/ShowLoader';
import { getHighlightedText } from 'src/utils/textUtils';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

export const TrimData = (data, length = 25) => {
  if (!data) return '-';
  if (typeof data !== 'string') return String(data);
  if (data.length <= length) return data;
  return data.substring(0, 15) + '...' + data.substring(data.length - 8);
};

export const maskData = (value, isMasked) => {
  if (isMasked && value) {
    const str = String(value);
    const maskLength = Math.ceil(str.length / 2);
    return '*'.repeat(maskLength) + str.substring(maskLength);
  }
  return value;
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const strHours = String(hours).padStart(2, '0');
    return `${day}-${month}-${year} ${strHours}:${minutes} ${ampm}`;
  } catch (err) {
    return dateStr;
  }
};

const getStatusChip = (status) => {
  const s = String(status || '').toLowerCase();
  switch (s) {
    case 'delivered':
      return (
        <Chip
          label="Delivered"
          size="small"
          sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.75rem' }}
        />
      );
    case 'read':
      return (
        <Chip
          label="Read"
          size="small"
          sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.75rem' }}
        />
      );
    case 'failed':
      return (
        <Chip
          label="Failed"
          size="small"
          sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.75rem' }}
        />
      );
    case 'pending':
    case 'submitted':
      return (
        <Chip
          label={s.charAt(0).toUpperCase() + s.slice(1)}
          size="small"
          sx={{ bgcolor: '#fff3e0', color: '#ef6c00', fontWeight: 600, fontSize: '0.75rem' }}
        />
      );
    case 'sent':
      return (
        <Chip
          label="Sent"
          size="small"
          sx={{ bgcolor: '#f5f5f5', color: '#616161', fontWeight: 600, fontSize: '0.75rem' }}
        />
      );
    default:
      return (
        <Chip
          label={status || '-'}
          size="small"
          sx={{ bgcolor: '#f5f5f5', color: '#616161', fontSize: '0.75rem' }}
        />
      );
  }
};

const FieldData = ({
  search = '',
  page = 1,
  limit = 20,
  onPaginationChange,
  refreshTrigger,
  data: propData,
  getData: propGetData,
}) => {
  const {
    fetchAllWhatsappCampaigns,
    fetchWhatsappCampaignDetails,
    activeBranchId,
    branchData,
    setShowMessage,
  } = useMain();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);

  // Popup & details states
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState(null);
  const [selectedCampaignPreview, setSelectedCampaignPreview] = useState(null);
  const [detailsData, setDetailsData] = useState(null);

  // Client filtering & pagination in popup
  const [clientPage, setClientPage] = useState(1);
  const [clientLimit, setClientLimit] = useState(10);
  const [clientFilterStatus, setClientFilterStatus] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const lastSearchRef = useRef('');

  const campaignGridTemplate = '1.6fr 1.2fr 1.3fr 0.7fr 0.8fr 0.7fr 0.7fr 0.7fr 0.9fr 0.8fr 0.8fr 0.7fr';
  const campaignGridRowStyle = {
    display: 'grid',
    gridTemplateColumns: campaignGridTemplate,
    alignItems: 'center',
    columnGap: '8px',
  };

  const fetchCampaigns = async () => {
    if (!activeBranchId) return;
    try {
      setLoading(true);
      const queryParams = {
        page,
        limit,
      };
      if (search && search.trim()) {
        queryParams.search = search.trim();
      }
      const res = await fetchAllWhatsappCampaigns(activeBranchId, queryParams);
      if (res?.statusCode === 200 || res?.data) {
        const list = res?.data?.campaigns || [];
        setCampaigns(list);
        if (onPaginationChange && res?.data?.pagination) {
          onPaginationChange(res.data.pagination);
        }
      } else {
        setShowMessage?.({
          message: res?.message || 'Failed to fetch campaigns',
          messageType: 'error',
        });
      }
    } catch (error) {
      setShowMessage?.({
        message: error.message || 'Error fetching campaigns',
        messageType: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propData) {
      setCampaigns(propData);
      return;
    }
    if (activeBranchId) {
      fetchCampaigns();
    }
  }, [activeBranchId, page, limit, search, refreshTrigger, propData]);

  // Fetch campaign details via API
  const fetchCampaignDetailsApi = async (batchId, cPage = 1, cLimit = 10, cStatus = '', cSearch = '') => {
    if (!activeBranchId || !batchId) return;
    lastSearchRef.current = cSearch || '';
    try {
      setDetailsLoading(true);
      const queryParams = {
        page: cPage,
        limit: cLimit,
      };
      if (cStatus) {
        queryParams.status = cStatus;
      }
      if (cSearch && cSearch.trim()) {
        queryParams.search = cSearch.trim();
      }

      const res = await fetchWhatsappCampaignDetails(activeBranchId, batchId, queryParams);
      if (res?.statusCode === 200 || res?.data) {
        setDetailsData(res?.data);
      } else {
        setShowMessage?.({
          message: res?.message || 'Failed to fetch campaign details',
          messageType: 'error',
        });
      }
    } catch (error) {
      setShowMessage?.({
        message: error.message || 'Error fetching campaign details',
        messageType: 'error',
      });
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleViewDetails = (item) => {
    setSelectedBatchId(item.batchId);
    setSelectedCampaignPreview(item);
    setDetailsData(null);
    setDetailsOpen(true);
    setClientPage(1);
    setClientFilterStatus('');
    setClientSearch('');
    fetchCampaignDetailsApi(item.batchId, 1, 10, '', '');
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedBatchId(null);
    setSelectedCampaignPreview(null);
    setDetailsData(null);
  };

  const handleClientStatusChange = (status) => {
    const nextStatus = clientFilterStatus === status ? '' : status;
    setClientFilterStatus(nextStatus);
    setClientPage(1);
    fetchCampaignDetailsApi(selectedBatchId, 1, clientLimit, nextStatus, clientSearch);
  };

  const handleClientSearchChange = (e) => {
    const val = e.target.value;
    setClientSearch(val);
  };

  const handleClientSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      setClientPage(1);
      fetchCampaignDetailsApi(selectedBatchId, 1, clientLimit, clientFilterStatus, clientSearch);
    }
  };

  const handleClearClientSearch = () => {
    setClientSearch('');
    setClientPage(1);
    fetchCampaignDetailsApi(selectedBatchId, 1, clientLimit, clientFilterStatus, '');
  };

  const handleClientPageChange = (event, newPage) => {
    setClientPage(newPage);
    fetchCampaignDetailsApi(selectedBatchId, newPage, clientLimit, clientFilterStatus, clientSearch);
  };

  const displayData = React.useMemo(() => {
    if (!search || !search.trim()) return campaigns;
    const lower = search.toLowerCase().trim();
    return campaigns.filter((item) => {
      const nameMatch = item?.campaignName?.toLowerCase().includes(lower);
      const templateMatch = item?.templateName?.toLowerCase().includes(lower);
      return nameMatch || templateMatch;
    });
  }, [campaigns, search]);

    useEffect(() => {
    if (!detailsOpen || !selectedBatchId) return;
    if (clientSearch === lastSearchRef.current) return;

    const timer = setTimeout(() => {
      if (clientSearch === lastSearchRef.current) return;
      setClientPage(1);
      fetchCampaignDetailsApi(
        selectedBatchId,
        1,
        clientLimit,
        clientFilterStatus,
        clientSearch,
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [clientSearch]);

  if (loading) {
    return <ShowLoader value={loading} />;
  }

  // Active details objects
  const campaignInfo = detailsData?.campaign || selectedCampaignPreview || {};
  const summaryInfo = detailsData?.summary || selectedCampaignPreview?.summary || {};
  const statusBreakdown = detailsData?.statusBreakdown || [];
  const failureReasons = detailsData?.failureReasons || [];
  const clientList = detailsData?.clients || [];
  const clientPagination = detailsData?.pagination || { page: 1, limit: 10, totalClients: 0, totalPages: 1 };

  return (
    <Box sx={{ overflowX: 'auto', width: '100%' }}>
      {displayData.length > 0 ? (
        <table className="table-container mt-3" style={{ minWidth: '1150px', width: '100%' }}>
          <thead className="table-thead">
            <tr style={campaignGridRowStyle}>
              {[
                'Campaign Name',
                // 'Template',
                'Sent At',
                'Total',
                'Delivered',
                'Read',
                'Failed',
                'Pending',
                'Delivery %',
                'Read %',
                'Total Cost',
                'Actions',
              ].map((header, index) => (
                <th key={index} style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="table-tbody">
            {displayData.map((item, index) => {
              const summary = item?.summary || {};
              const total = summary.total ?? 0;
              const delivered = summary.delivered ?? 0;
              const read = summary.read ?? 0;
              const failed = summary.failed ?? 0;
              const pending = summary.pending ?? 0;
              const deliveryRate = summary.deliveryRate ?? 0;
              const readRate = summary.readRate ?? 0;
              const totalCost = summary.totalCost ?? 0;

              return (
                <tr key={item.batchId || index} style={campaignGridRowStyle}>
                  {/* Campaign Name */}
                  <td
                    style={{
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={item?.campaignName}
                  >
                    <Tooltip title={item?.campaignName || ''}>
                      <span style={{ fontWeight: 500, color: '#333' }}>
                        {getHighlightedText(item?.campaignName || '-', search)}
                      </span>
                    </Tooltip>
                  </td>

                  {/* Template Name */}
                  {/* <td
                    style={{
                      textAlign: 'center',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                    title={item?.templateName}
                  >
                    <Chip
                      label={item?.templateName || '-'}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: '#6174DD',
                        color: '#6174DD',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        maxWidth: '120px',
                      }}
                    />
                  </td> */}

                  {/* Sent Date */}
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap', fontSize: '0.82rem' }}>
                    {formatDateTime(item?.sentAt)}
                  </td>

                  {/* Total */}
                  <td style={{ textAlign: 'center', fontWeight: 600 }}>
                    {total}
                  </td>

                  {/* Delivered */}
                  <td style={{ textAlign: 'center' }}>
                    <Chip
                      label={delivered}
                      size="small"
                      sx={{
                        bgcolor: '#e8f5e9',
                        color: '#2e7d32',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        minWidth: '38px',
                      }}
                    />
                  </td>

                  {/* Read */}
                  <td style={{ textAlign: 'center' }}>
                    <Chip
                      label={read}
                      size="small"
                      sx={{
                        bgcolor: '#e3f2fd',
                        color: '#1565c0',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        minWidth: '38px',
                      }}
                    />
                  </td>

                  {/* Failed */}
                  <td style={{ textAlign: 'center' }}>
                    <Chip
                      label={failed}
                      size="small"
                      sx={{
                        bgcolor: failed > 0 ? '#ffebee' : '#f5f5f5',
                        color: failed > 0 ? '#c62828' : '#757575',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        minWidth: '38px',
                      }}
                    />
                  </td>

                  {/* Pending */}
                  <td style={{ textAlign: 'center' }}>
                    <Chip
                      label={pending}
                      size="small"
                      sx={{
                        bgcolor: pending > 0 ? '#fff3e0' : '#f5f5f5',
                        color: pending > 0 ? '#ef6c00' : '#757575',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        minWidth: '38px',
                      }}
                    />
                  </td>

                  {/* Delivery Rate */}
                  <td style={{ textAlign: 'center', fontWeight: 500 }}>
                    {deliveryRate}%
                  </td>

                  {/* Read Rate */}
                  <td style={{ textAlign: 'center', fontWeight: 500 }}>
                    {readRate}%
                  </td>

                  {/* Total Cost */}
                  <td style={{ textAlign: 'center', fontWeight: 600, color: '#333' }}>
                    ₹{totalCost}
                  </td>

                  {/* Actions */}
                  <td style={{ textAlign: 'center' }}>
                    <IconButton
                      onClick={() => handleViewDetails(item)}
                      color="primary"
                      title="View Campaign Details"
                      size="small"
                    >
                      <AiOutlineInfoCircle style={{ color: '#6174DD', fontSize: '1.25rem' }} />
                    </IconButton>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <Box sx={{ textAlign: 'center', py: 5, color: '#666' }}>
          <Typography variant="body1">No campaign data found</Typography>
        </Box>
      )}

      {/* WhatsApp Campaign Details Popup */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '14px',
            maxHeight: '92vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid #e0e0e0',
            pb: 1.5,
            pt: 2,
            px: 3,
            backgroundColor: '#FAFBFF',
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <WhatsAppIcon sx={{ color: '#25D366', fontSize: '1.8rem' }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#2c3e50', lineHeight: 1.2 }}>
                WhatsApp Campaign Details
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {campaignInfo?.campaignName || '-'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDetails} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3, backgroundColor: '#FAFAFA' }}>
          {detailsLoading && !detailsData ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
              <CircularProgress sx={{ color: '#6174DD', mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading campaign details...
              </Typography>
            </Box>
          ) : (
            <Box>
              {/* Campaign Overview Card */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  mb: 2.5,
                  borderRadius: '10px',
                  border: '1px solid #e6e6e6',
                  backgroundColor: '#FFF',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Campaign Name
                    </Typography>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333' }}>
                      {campaignInfo?.campaignName || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">
                      Template Name
                    </Typography>
                    <Box mt={0.3}>
                      <Chip
                        label={campaignInfo?.templateName || '-'}
                        size="small"
                        sx={{
                          bgcolor: '#f0f3ff',
                          color: '#6174DD',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                      {campaignInfo?.category || '-'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      Sent Date & Time
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(campaignInfo?.sentAt)}
                    </Typography>
                  </Grid>

                  {/* <Grid item xs={12} sm={6} md={2}>
                    <Typography variant="caption" color="text.secondary">
                      Last Synced
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatDateTime(summaryInfo?.lastSyncedAt)}
                    </Typography>
                  </Grid> */}

                  <Grid item xs={12}>
                    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
                      {/* <Typography variant="caption" color="text.secondary">
                        Batch ID:
                      </Typography> */}
                      {/* <Typography
                        variant="caption"
                        sx={{
                          fontFamily: 'monospace',
                          bgcolor: '#f5f5f5',
                          px: 1,
                          py: 0.3,
                          borderRadius: '4px',
                          color: '#555',
                          wordBreak: 'break-all',
                        }}
                      >
                        {campaignInfo?.batchId || '-'}
                      </Typography> */}

                      {campaignInfo?.headerType && (
                        <Chip
                          label={`Header: ${campaignInfo.headerType}`}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Delivery Stats Metric Cards */}
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#333' }}>
                Delivery & Status Summary
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ p: 1.8, bgcolor: '#f4f6fd', borderRadius: '10px', textAlign: 'center', border: '1px solid #e1e7fa' }}>
                    <Typography variant="caption" color="text.secondary">
                      Total
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#6174DD', mt: 0.5 }}>
                      {summaryInfo?.total ?? 0}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ p: 1.8, bgcolor: '#f5f5f5', borderRadius: '10px', textAlign: 'center', border: '1px solid #e8e8e8' }}>
                    <Typography variant="caption" color="text.secondary">
                      Sent
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#424242', mt: 0.5 }}>
                      {summaryInfo?.sent ?? 0}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ p: 1.8, bgcolor: '#e8f5e9', borderRadius: '10px', textAlign: 'center', border: '1px solid #c8e6c9' }}>
                    <Typography variant="caption" color="text.secondary">
                      Delivered
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2e7d32', mt: 0.5 }}>
                      {summaryInfo?.delivered ?? 0}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ p: 1.8, bgcolor: '#e3f2fd', borderRadius: '10px', textAlign: 'center', border: '1px solid #bbdefb' }}>
                    <Typography variant="caption" color="text.secondary">
                      Read
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#1565c0', mt: 0.5 }}>
                      {summaryInfo?.read ?? 0}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ p: 1.8, bgcolor: '#fff3e0', borderRadius: '10px', textAlign: 'center', border: '1px solid #ffe0b2' }}>
                    <Typography variant="caption" color="text.secondary">
                      Pending
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#ef6c00', mt: 0.5 }}>
                      {summaryInfo?.pending ?? 0}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={6} sm={4} md={2}>
                  <Box sx={{ p: 1.8, bgcolor: '#ffebee', borderRadius: '10px', textAlign: 'center', border: '1px solid #ffcdd2' }}>
                    <Typography variant="caption" color="text.secondary">
                      Failed
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#c62828', mt: 0.5 }}>
                      {summaryInfo?.failed ?? 0}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Engagement & Financial Metrics */}
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 2.5,
                  borderRadius: '10px',
                  border: '1px solid #e6e6e6',
                  backgroundColor: '#FFF',
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Delivery Rate
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                      {summaryInfo?.deliveryRate ?? 0}%
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Read Rate
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1565c0' }}>
                      {summaryInfo?.readRate ?? 0}%
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Total Cost
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                      ₹{summaryInfo?.totalCost ?? 0}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Cost Per Message
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      ₹{summaryInfo?.costPerMessage ?? summaryInfo?.totalCost ?? 0}
                    </Typography>
                  </Grid>

                  {/* <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Cost Per Read
                    </Typography>
                    <Typography variant="body2">
                      {summaryInfo?.costPerRead ? `₹${summaryInfo.costPerRead}` : '-'}
                    </Typography>
                  </Grid> */}

                  {/* <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Avg Minutes To Read
                    </Typography>
                    <Typography variant="body2">
                      {summaryInfo?.avgMinutesToRead !== null && summaryInfo?.avgMinutesToRead !== undefined
                        ? `${summaryInfo.avgMinutesToRead} min`
                        : '-'}
                    </Typography>
                  </Grid> */}

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      First Read At
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(summaryInfo?.firstReadAt)}
                    </Typography>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      Last Read At
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(summaryInfo?.lastReadAt)}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Failure Reasons Alert (if any) */}
              {failureReasons && failureReasons.length > 0 && (
                <Alert severity="error" sx={{ mb: 2.5, borderRadius: '8px' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Message Delivery Failures:
                  </Typography>
                  {failureReasons.map((fr, idx) => (
                    <Typography key={idx} variant="body2">
                      • {fr.reason || 'Unknown error'}: {fr.count} message(s)
                    </Typography>
                  ))}
                </Alert>
              )}

              {/* Clients Recipients Section */}
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: '10px',
                  border: '1px solid #e6e6e6',
                  backgroundColor: '#FFF',
                }}
              >
                {/* Section Header: Title, Search, and Status Filter Chips */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 2,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#333' }}>
                      Recipients
                    </Typography>
                    <Chip
                      label={clientPagination.totalClients || clientList.length}
                      size="small"
                      sx={{ bgcolor: '#f0f3ff', color: '#6174DD', fontWeight: 700 }}
                    />
                  </Box>

                  {/* Search Input for Clients */}
                  <Box display="flex" alignItems="center" gap={1}>
                    <TextField
                      size="small"
                      placeholder="Search recipient..."
                      value={clientSearch}
                      onChange={handleClientSearchChange}
                      onKeyDown={handleClientSearchKeyDown}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: '#888' }} />
                          </InputAdornment>
                        ),
                        endAdornment: clientSearch ? (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={handleClearClientSearch}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                      }}
                      sx={{
                        width: '220px',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          height: '38px',
                        },
                      }}
                    />

                  </Box>
                </Box>

                {/* Status Filter Chips */}
                <Box display="flex" gap={1} flexWrap="wrap" sx={{ mb: 2 }}>
                  <Chip
                    label={`All (${summaryInfo?.total ?? clientList.length})`}
                    onClick={() => handleClientStatusChange('')}
                    color={clientFilterStatus === '' ? 'primary' : 'default'}
                    variant={clientFilterStatus === '' ? 'filled' : 'outlined'}
                    size="small"
                    sx={{
                      cursor: 'pointer',
                      fontWeight: 600,
                      ...(clientFilterStatus === '' && { bgcolor: '#6174DD' }),
                    }}
                  />

                  {statusBreakdown && statusBreakdown.length > 0 ? (
                    statusBreakdown.map((sb, idx) => {
                      const isSelected = clientFilterStatus.toLowerCase() === sb.status.toLowerCase();
                      return (
                        <Chip
                          key={idx}
                          label={`${sb.status.charAt(0).toUpperCase() + sb.status.slice(1)}: ${sb.count} (${sb.percent}%)`}
                          onClick={() => handleClientStatusChange(sb.status)}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={{
                            cursor: 'pointer',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            ...(isSelected && { bgcolor: '#6174DD' }),
                          }}
                        />
                      );
                    })
                  ) : (
                    <>
                      <Chip
                        label={`Delivered (${summaryInfo?.delivered ?? 0})`}
                        onClick={() => handleClientStatusChange('delivered')}
                        variant={clientFilterStatus === 'delivered' ? 'filled' : 'outlined'}
                        color={clientFilterStatus === 'delivered' ? 'primary' : 'default'}
                        size="small"
                        sx={{ cursor: 'pointer' }}
                      />
                      <Chip
                        label={`Read (${summaryInfo?.read ?? 0})`}
                        onClick={() => handleClientStatusChange('read')}
                        variant={clientFilterStatus === 'read' ? 'filled' : 'outlined'}
                        color={clientFilterStatus === 'read' ? 'primary' : 'default'}
                        size="small"
                        sx={{ cursor: 'pointer' }}
                      />
                      <Chip
                        label={`Failed (${summaryInfo?.failed ?? 0})`}
                        onClick={() => handleClientStatusChange('failed')}
                        variant={clientFilterStatus === 'failed' ? 'filled' : 'outlined'}
                        color={clientFilterStatus === 'failed' ? 'primary' : 'default'}
                        size="small"
                        sx={{ cursor: 'pointer' }}
                      />
                    </>
                  )}
                </Box>

                {/* Clients Table */}
                <TableContainer
                  component={Box}
                  sx={{
                    border: '1px solid #eee',
                    borderRadius: '8px',
                    maxHeight: '380px',
                    overflowY: 'auto',
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ '& th': { bgcolor: '#f4f6fd', fontWeight: 600, color: '#333' } }}>
                        <TableCell align="center" sx={{ width: '50px' }}>#</TableCell>
                        <TableCell>Client Name</TableCell>
                        <TableCell>Contact Number</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Sent At</TableCell>
                        <TableCell align="center">Delivered At</TableCell>
                        <TableCell align="center">Read At</TableCell>
                        <TableCell align="center">Cost</TableCell>
                        <TableCell align="center">Variables</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailsLoading ? (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                            <CircularProgress size={28} sx={{ color: '#6174DD' }} />
                          </TableCell>
                        </TableRow>
                      ) : clientList.length > 0 ? (
                        clientList.map((client, idx) => {
                          const rowNum = (clientPagination.page - 1) * clientPagination.limit + idx + 1;
                          const variablesText =
                            client?.variables && Object.keys(client.variables).length > 0
                              ? Object.entries(client.variables)
                                  .map(([k, v]) => `${k}: ${v}`)
                                  .join(', ')
                              : null;

                          return (
                            <TableRow key={client.clientId || client.requestId || idx} hover>
                              <TableCell align="center" sx={{ color: '#888', fontSize: '0.8rem' }}>
                                {rowNum}
                              </TableCell>
                              <TableCell sx={{ fontWeight: 500, color: '#222' }}>
                                {client?.clientName || '-'}
                              </TableCell>
                              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                {typeof branchData?.isMasked === 'boolean' && branchData.isMasked
                                  ? maskData(client?.clientNumber, true)
                                  : client?.clientNumber || '-'}
                              </TableCell>
                              <TableCell align="center">
                                {getStatusChip(client?.status)}
                                {client?.failureReason && (
                                  <Tooltip title={`Reason: ${client.failureReason}`}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        color: '#c62828',
                                        fontSize: '0.7rem',
                                        cursor: 'help',
                                      }}
                                    >
                                      {client.failureReason}
                                    </Typography>
                                  </Tooltip>
                                )}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                {formatDateTime(client?.sentAt)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                {formatDateTime(client?.deliveredAt)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                                {formatDateTime(client?.readAt)}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: 500 }}>
                                ₹{client?.cost ?? 0}
                              </TableCell>
                              <TableCell align="center">
                                {variablesText ? (
                                  <Tooltip title={variablesText}>
                                    <Chip
                                      label="View"
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.7rem', cursor: 'pointer', height: '22px' }}
                                    />
                                  </Tooltip>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 4, color: '#888' }}>
                            No recipients found for this filter
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Client Pagination */}
                {clientPagination.totalPages > 1 && (
                  <Box display="flex" justifyContent="flex-end" alignItems="center" mt={2}>
                    <Pagination
                      count={clientPagination.totalPages || 1}
                      page={clientPagination.page || clientPage}
                      color="primary"
                      size="small"
                      onChange={handleClientPageChange}
                    />
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5, backgroundColor: '#FAFBFF', borderTop: '1px solid #e0e0e0' }}>
          <Button
            onClick={handleCloseDetails}
            variant="contained"
            sx={{
              backgroundColor: '#6174DD',
              '&:hover': { backgroundColor: '#4f61c4' },
              textTransform: 'none',
              borderRadius: '7px',
              px: 3,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FieldData;
